import SSNModal from '@mortgage-pos/ui/components/SSNModal'
import UpdateRates from '@mortgage-pos/ui/components/UpdateRates'
import Loading from '@mortgage-pos/ui/components/Loading'
import InfoModal from '@mortgage-pos/ui/components/InfoModal'
import rollbar from '@mortgage-pos/ui/services/rollbar'
import { useNavigate } from 'react-router-dom'
import LoanOfficerPricing from '@mortgage-pos/ui/components/LoanOfficerPricing'
import React, { FC, useEffect, useState } from 'react'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import Button, { ButtonVariants } from '@mortgage-pos/ui/components/Button'
import PricingInfoAccordion from '@mortgage-pos/ui/components/PricingInfoAccordion'
import { useStoreState, useStoreActions } from '@mortgage-pos/ui/store'
import { EducationSection } from '@mortgage-pos/ui/components/pricing/EducationSection'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import { FEATURE_FLAG_TYPES, rateCodesMap } from '@mortgage-pos/data'
import TabbedFeeEstimateModal from '@mortgage-pos/ui/components/TabbedFeeEstimateModal'
import {
  LoanPurpose,
  Rate,
  Routes,
  Status,
  ExperienceType,
} from '@mortgage-pos/types'
import RateCardMasonry, {
  RateCardVariant,
} from '@mortgage-pos/ui/components/pricing/RateCardMasonry'
import { WhyRefiPricingLogic } from '@mortgage-pos/ui/utils/WhyRefiPricingLogic'
import questionMark from '@mortgage-pos/ui/assets/question-mark.svg'
import incense from '@mortgage-pos/ui/incense'
import useMediaQuery from '@shared-fe/hooks/useMediaQuery'

import {
  LOCardVariant,
  LoanOfficerCard,
} from '@mortgage-pos/ui/components/pricing/LoanOfficerPricingCard'

import {
  lockScroll,
  unlockScroll,
  extractGQLErrors,
  deriveLoanTermInMonths,
} from '@mortgage-pos/utils'

const LO_CARD_INDEX = 3

const NtlRateSelection: FC = () => {
  const navigate = useNavigate()

  const { loanOfficer } = useStoreState((state) => state.loanOfficer)
  const { applicationId } = useStoreState((state) => state.application)

  const { statusesMap, isPrePricingFlow } = useStoreState(
    (state) => state.questionnaire
  )

  const { isRequestingRates, sortedRates } = useStoreState(
    (state) => state.rates
  )

  const { mergedAnswers, bankrateAnswers } = useStoreState(
    (state) => state.answers
  )

  const { next } = useStoreActions((state) => state.questionnaire)

  const { updateStatus, setExperienceType } = useStoreActions(
    (state) => state.application
  )

  const { requestRates, submitLockForm } = useStoreActions(
    (state) => state.rates
  )

  const { formSubmitted } = useStoreActions((state) => state.tracking)

  // Modal state
  const [isFeeModalOpen, setFeeModalOpen] = useState(false)
  const [isRecommendedRateFeeModalOpen, setIsRecommendedRateFeeModalOpen] =
    useState(false)
  const [feeModalIndex, setFeeModalIndex] = useState(null)
  const [showRefreshRatesModal, setShowRefreshRatesModal] = useState(false)
  const [showDefinitionsModal, setShowDefinitionsModal] = useState(false)

  // display state
  const [currentlySelectedRate, setCurrentlySelectedRate] = useState<{}>()
  const [showLocards, setShowLoCards] = useState(false)

  const [ratesRefreshed, setRatesRefreshed] = useState(false)
  const [recommendedRate, setRecommendedRate] = useState(null)
  const [displayedRates, setDisplayedRates] = useState([])
  const [termSelected, setTermSelected] = useState('F15')
  const [showMoreRates, setShowMoreRates] = useState(4)
  const { refiReason, previouslySelectedRate } = mergedAnswers

  const whyRefiPricingLogic = new WhyRefiPricingLogic(refiReason, {
    sortedRates,
    bankrateSelectedRate: previouslySelectedRate,
  })

  const matchedRates = whyRefiPricingLogic?.findMatchedRates()

  const isRefi = mergedAnswers?.loanPurpose === LoanPurpose.Refinance
  const isMobile = useMediaQuery('(max-width: 950px)', true, false)

  useEffect(() => {
    setExperienceType(ExperienceType.Ntl)
  }, [setExperienceType])

  useEffect(() => {
    setShowMoreRates(4)
  }, [termSelected])

  useEffect(() => {
    if (!sortedRates || !sortedRates.length) {
      rollbar.error('Attempted to show NTL rates page with no rates in state', {
        applicationId,
      })
      return navigate(`/${Routes.APP_1003}/no-rates`)
    }

    const matchedRate = findBankrateMatchedRate(sortedRates)
    setRecommendedRate(matchedRate)
  }, [])

  /**
   * sets displayed rates based on the filter. limit of five in the array as default
   */
  useEffect(() => {
    const filteredRatesByTerm = []
    const acceptedRateTerms = ['F10', 'F15', 'F20', 'F25', 'F30', 'F40']

    if (sortedRates.length) {
      sortedRates.forEach((rate) => {
        if (
          !filteredRatesByTerm[rate.term] &&
          acceptedRateTerms.includes(rate.term)
        ) {
          filteredRatesByTerm[rate.term] = [rate]
        } else {
          filteredRatesByTerm[rate.term].push(rate)
        }
      })

      filteredRatesByTerm['Best Matches'] = matchedRates?.rates ?? []
      setTermSelected('Best Matches')

      setDisplayedRates(filteredRatesByTerm)
    }
  }, [sortedRates])

  /**
   *  findBankrateMatchedRate filters out the specific rate matched by Bankrate.
   * @param {Array<Rate>} rates
   * @returns {Rate | null}
   */
  const findBankrateMatchedRate = (rates) => {
    const { previouslySelectedRate } = bankrateAnswers

    if (previouslySelectedRate?.rate && previouslySelectedRate?.termInMonths) {
      const matchedRate = rates.find(({ rate, term }) => {
        const termName = rateCodesMap[term]
        const termInMonths = deriveLoanTermInMonths(termName)

        return (
          rate === previouslySelectedRate.rate &&
          termInMonths === previouslySelectedRate.termInMonths
        )
      })

      if (matchedRate) {
        // Switch to correct rate term for previously selected BR rate
        setTermSelected(matchedRate.term)

        return matchedRate
      }
    }

    return null
  }

  const handleTalkToExpertClick = () => {
    newRelic.increment('ntl_rate_selection.talk_to_an_expert.click')

    setShowLoCards(true)
    updateStatus(Status.CallRequested)
  }

  const handleGoBackToRatesClick = () => {
    setShowLoCards(false)
    setShowRefreshRatesModal(false)
    setFeeModalOpen(false)
    setIsRecommendedRateFeeModalOpen(false)
    setFeeModalIndex(null)
    setShowMoreRates(4)
    unlockScroll()
  }

  const handleLockRateClick = (rate: Rate): void => {
    setCurrentlySelectedRate(rate)

    if (isPrePricingFlow) {
      submitLockFormAndUpdateStatus(rate)
      return
    }

    if (!ratesRefreshed) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })

      setShowRefreshRatesModal(true)
      lockScroll()
    } else {
      submitLockFormAndUpdateStatus(rate)
    }
  }

  const handleTermClick = (term) => {
    setTermSelected(term)
  }

  const refreshRates = async () => {
    handleGoBackToRatesClick()
    setRatesRefreshed(true)

    try {
      // @ts-ignore
      const ratesResponse = await requestRates()

      if (!ratesResponse.length) {
        return navigate(`/${Routes.APP_1003}/no-rates`)
      }
    } catch (e) {
      incense(e)
        .details({
          name: 'ntlRateSelection.refreshRates',
          message: 'Error while refreshing rates',
        })
        .sensitive({
          mergedAnswers,
        })
        .error()

      return navigate(`/${Routes.APP_1003}/no-rates`)
    }
  }

  const handleSSNSubmit = (areScoresEqual) => {
    if (areScoresEqual) {
      submitLockFormAndUpdateStatus(currentlySelectedRate)
    } else {
      refreshRates()
    }
  }

  const submitLockFormAndUpdateStatus = async (rate) => {
    try {
      await submitLockForm(rate)
      await updateStatus(statusesMap.lock)
    } catch (error) {
      rollbar.error(
        'submitLockFormAndUpdateStatus - Failed to submit lock form',
        {
          applicationId,
          sensitive: {
            error,
            rate,
            gqlErrors: extractGQLErrors(error),
          },
        }
      )

      navigate(`/${Routes.APP_1003}/error`)
    }

    formSubmitted()
    next(mergedAnswers)
  }

  if (isRequestingRates || !sortedRates.length) {
    return <Loading loaderType="house" shouldWrap={false} />
  }

  return (
    <>
      {showDefinitionsModal ? (
        <InfoModal
          onClose={() => setShowDefinitionsModal(false)}
          modalVariant="definitions"
        />
      ) : null}

      {showLocards ? (
        <LoanOfficerPricing
          loanOfficer={loanOfficer}
          handleGoBackToRatesClick={handleGoBackToRatesClick}
        />
      ) : (
        <>
          {ratesRefreshed ? (
            <div className="ntl-rate-selection__refresh-info-wrapper">
              <aside className="ntl-rate-selection__refresh-info-block">
                We refreshed your pricing options. Please select the rate you
                would like to lock.
                <button onClick={() => setRatesRefreshed(false)}>
                  <img
                    src="@mortgage-pos/ui/assets/black-x.svg"
                    alt="Close button"
                  />
                </button>
              </aside>
            </div>
          ) : null}

          <div className="ntl-rate-selection__update-wrapper">
            {isRefi ? (
              <div className="ntl-rate-selection__update-container">
                <UpdateRates refreshRates={refreshRates} />
              </div>
            ) : null}
          </div>
          <div className="ntl-rate-selection__update-container-terms">
            <div className="ntl-rate-selection__update-container-terms-buttons">
              {Object.keys(displayedRates)
                .reverse()
                .map((key) => (
                  <Button
                    className={
                      termSelected === key
                        ? 'ntl-rate-selection__button --selected'
                        : 'ntl-rate-selection__button'
                    }
                    content={
                      key === 'Best Matches'
                        ? 'Best Matches'
                        : rateCodesMap[key]
                    }
                    key={key}
                    onClick={() => handleTermClick(key)}
                  />
                ))}
            </div>
            {!isMobile ? (
              <div
                className="ntl-rate-selection__terms-definition"
                onClick={() => setShowDefinitionsModal(true)}
              >
                Terms
                <img
                  className="ntl-rate-selection__question-mark-icon"
                  src={questionMark}
                  alt="question mark icon"
                />
              </div>
            ) : null}
          </div>
          <React.Fragment>
            <div className="rate-card-container">
              {displayedRates[termSelected]?.map((rate, index) => {
                if (index > showMoreRates) return null
                return (
                  <React.Fragment key={index}>
                    {index === LO_CARD_INDEX ? (
                      <LoanOfficerCard
                        variant={LOCardVariant.card}
                        heading={'Need help?'}
                        body={'Discuss more options with an expert today.'}
                        onCtaClick={handleTalkToExpertClick}
                      />
                    ) : null}
                    <RateCardMasonry
                      handleLockRateClick={handleLockRateClick}
                      handleFeeBreakdownClick={() => {
                        setFeeModalOpen(true)
                        setFeeModalIndex(index)
                        lockScroll()
                      }}
                      rate={rate}
                      variant={
                        termSelected === 'Best Matches'
                          ? RateCardVariant.Recommendation
                          : RateCardVariant.Simple
                      }
                      submitButtonText="Lock in this rate"
                      rateType={matchedRates?.rateItems?.[index]}
                    />
                  </React.Fragment>
                )
              })}
            </div>

            {/* Show the "Show More Rates" button if there are undisplayed results */}
            {displayedRates[termSelected]?.length > showMoreRates ? (
              <div className={'rate-card-container__show-more-container'}>
                <Button
                  onClick={() => {
                    setShowMoreRates((n) => n + 6)
                  }}
                  content={'Show more'}
                  variant={ButtonVariants.next}
                  className={'rate-card-container__show-more-btn'}
                />
              </div>
            ) : null}

            <EducationSection onTalkToExpertClick={handleTalkToExpertClick} />
          </React.Fragment>
        </>
      )}
      <PricingInfoAccordion />
      {showRefreshRatesModal ? (
        <SSNModal
          onClose={handleGoBackToRatesClick}
          onSubmit={handleSSNSubmit}
          isNTL={true}
        />
      ) : null}

      {isFeeModalOpen ? (
        <TabbedFeeEstimateModal
          onClose={handleGoBackToRatesClick}
          rate={displayedRates[termSelected][feeModalIndex]}
        />
      ) : null}

      {isRecommendedRateFeeModalOpen ? (
        <TabbedFeeEstimateModal
          onClose={handleGoBackToRatesClick}
          rate={recommendedRate}
        />
      ) : null}
    </>
  )
}
export default NtlRateSelection
